import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useExpandedFdocContentContext } from '../ExpandedFdocContentContext.js';
import { usePDFContent } from './PDF.js';
import { isInMobile, downloadFile } from '@fabric-app/compat';
import { IconPrint } from '@fabric-app/icons/old';
import { styled } from 'styled-components';
import { toast } from '../../../../store/alerts.js';
import { IconNavigational24Minus, IconNavigational24Plus, IconLinkArrow, IconDesktop16MiscDownload } from '@fabric-app/icons/svg';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { usePDFViewControls } from './PDFViewControls.js';
import { Button } from '@fabric-app/ui/components/Button';
import { SPAN } from '@fabric-app/ui/components/Typography';
import { formatPercentage } from '@fabric-app/utils/number';
import { cssVar } from '@fabric-app/ui/theme/variables';

const ZoomPageWrapper = styled.div.withConfig({
  displayName: "HeaderControls__ZoomPageWrapper",
  componentId: "sc-b09a5bf6-0"
})([
  `display:flex;align-items:center;justify-content:space-between;width:138px;*{user-select:none !important;}`
]);
const PageTracker = styled.div.withConfig({
  displayName: "HeaderControls__PageTracker",
  componentId: "sc-b09a5bf6-1"
})([
  `display:flex;gap:8px;color:`,
  `;font-size:16px;font-weight:400;white-space:nowrap;font-variant-numeric:tabular-nums;`
], cssVar["color-text-tertiary"]);
const HeaderControls = ({ pdf, isOwner, fileName, storedFileUrl }) => {
  const { topHeaderControlsRef, topHeaderTitleRef, setExtraDropdownMenuItems } = useExpandedFdocContentContext();
  const { totalPages, currentPage } = usePDFContent();
  const { zoomLevel, setZoomLevel } = usePDFViewControls();
  const onDownload = async () => {
    try {
      if (isInMobile()) {
        if (!storedFileUrl) return;
        downloadFile(storedFileUrl, fileName, "application/pdf");
        return;
      }
      toast({
        content: "Downloading..."
      });
      const data = await pdf.getData();
      const link = document.createElement("a");
      const blob = new Blob([
        data
      ], {
        type: "application/pdf"
      });
      const objectUrl = window.URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }, 100);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
  };
  const pageInfo = topHeaderTitleRef?.current ? /* @__PURE__ */ createPortal(/* @__PURE__ */ jsxs(PageTracker, {
    children: [
      /* @__PURE__ */ jsx("span", {
        children: "/"
      }),
      /* @__PURE__ */ jsxs("span", {
        children: [
          "Page ",
          totalPages ? currentPage : 0,
          " of ",
          totalPages
        ]
      })
    ]
  }), topHeaderTitleRef.current) : null;
  const zoomControls = topHeaderControlsRef ? /* @__PURE__ */ createPortal(/* @__PURE__ */ jsx(Fragment, {
    children: /* @__PURE__ */ jsxs(ZoomPageWrapper, {
      children: [
        /* @__PURE__ */ jsx(Button, {
          variant: "bg-secondary-text-primary",
          shape: "square",
          onClick: () => {
            setZoomLevel((prev) => prev / 1.5);
          },
          children: /* @__PURE__ */ jsx(IconNavigational24Minus, {})
        }),
        /* @__PURE__ */ jsx(SPAN, {
          color: "tertiary",
          onClick: () => {
            setZoomLevel(1);
          },
          children: formatPercentage(zoomLevel)
        }),
        /* @__PURE__ */ jsx(Button, {
          variant: "bg-secondary-text-primary",
          shape: "square",
          onClick: () => {
            setZoomLevel((prev) => prev * 1.5);
          },
          children: /* @__PURE__ */ jsx(IconNavigational24Plus, {})
        })
      ]
    })
  }), topHeaderControlsRef) : null;
  const iframeRef = useRef(null);
  useEffect(() => {
    return () => {
      if (!iframeRef.current) return;
      document.body.removeChild(iframeRef.current);
    };
  }, []);
  const onPrint = async () => {
    if (!iframeRef.current) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      iframeRef.current = iframe;
    }
    const data = await pdf.getData();
    const blob = new Blob([
      data
    ], {
      type: "application/pdf"
    });
    const objectUrl = window.URL.createObjectURL(blob);
    iframeRef.current.src = objectUrl;
    iframeRef.current.onload = () => {
      try {
        iframeRef.current?.contentWindow?.print();
      } catch (error) {
        console.error("Error printing PDF:", error);
      }
    };
  };
  const onOpenExternal = async () => {
    const data = await pdf.getData();
    const blob = new Blob([
      data
    ], {
      type: "application/pdf"
    });
    const objectUrl = window.URL.createObjectURL(blob);
    window.open(objectUrl);
    window.URL.revokeObjectURL(objectUrl);
  };
  useClientLayoutEffect(() => {
    setExtraDropdownMenuItems([
      {
        type: "item",
        key: "open-external",
        onSelect: onOpenExternal,
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(IconLinkArrow, {
              style: {
                width: 16,
                height: 12
              }
            }),
            " Open in browser"
          ]
        })
      },
      {
        type: "item",
        key: "download",
        onSelect: onDownload,
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(IconDesktop16MiscDownload, {}),
            " Download"
          ]
        })
      },
      {
        type: "item",
        key: "print",
        onSelect: onPrint,
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(IconPrint, {
              style: {
                width: 16,
                height: 12
              }
            }),
            " Print"
          ]
        })
      },
      ...isOwner ? [
        {
          type: "separator",
          key: "separator"
        }
      ] : []
    ]);
    return () => {
      setExtraDropdownMenuItems([]);
    };
  }, []);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      pageInfo,
      " ",
      zoomControls
    ]
  });
};

export { HeaderControls as default };
//# sourceMappingURL=HeaderControls.js.map
